<template>
  <div class="dashboard-container">
    <div v-if="loading" class="progress-bar" :style="{ width: progress + '%' }"></div>

    <div class="container">
      <div class="mb-2 page-title">
        <i class="bi bi-images"></i>
        {{ isEdit ? "Edit Slider" : "Add Slider" }}
      </div>

      <form @submit.prevent="submitForm" class="admin-form mt-4">
        <div class="row">
          <div class="col-lg-12">
            <!-- Image -->
            <div class="mb-3">
              <label class="form-label">Image</label>

              <input
                type="file"
                class="form-control"
                accept="image/*"
                @change="onFileChange"
                :class="{ 'is-invalid': errors.image }"
              />

              <div class="invalid-feedback">{{ errors.image }}</div>

              <div v-if="previewUrl" class="mt-3">
                <div class="preview-wrap">
                  <img :src="previewUrl" class="preview-img" alt="preview" />
                </div>
                <small class="text-muted d-block mt-2" v-if="isEdit && !selectedFile">
                  * Ini preview dari data lama. Upload file baru kalau mau ganti.
                </small>
              </div>
            </div>

            <!-- Sort -->
            <div class="mb-3">
              <label class="form-label">Urutan (Sort)</label>
              <input
                type="number"
                min="0"
                class="form-control"
                v-model.number="form.sort"
                :class="{ 'is-invalid': errors.sort }"
                placeholder="0"
              />
              <div class="invalid-feedback">{{ errors.sort }}</div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-12 text-center">
            <button type="submit" class="button button-primary" :disabled="loading">
              <i class="bi bi-floppy"></i>
              <span>{{ isEdit ? "Update Slider" : "Add Slider" }}</span>
            </button>

           
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";
import API from "@/config/api";

axios.defaults.baseURL = API.API_BASE_URL;

export default {
  name: "SliderForm",
  props: {
    isEdit: { type: Boolean, default: false },
    sliderId: { type: [String, Number], default: null },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      form: {
        sort: 0,
      },
      selectedFile: null,
      previewUrl: "",
      errors: {},
      loading: false,
      progress: 0,

      // ✅ jangan pake prefix underscore biar ESLint gak ngamuk
      sliderIdLocal: null,
    };
  },
  created() {
    this.sliderIdLocal = this.sliderId ?? this.$route?.params?.id ?? null;

    if (this.isEdit && this.sliderIdLocal) {
      this.fetchDetail(this.sliderIdLocal);
    }
  },
  watch: {
    "$route.params.id": {
      handler(newId) {
        if (this.isEdit && newId && String(newId) !== String(this.sliderIdLocal)) {
          this.sliderIdLocal = newId;
          this.fetchDetail(newId);
        }
      },
    },
  },
  methods: {
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      };
    },

    getImageUrl(slider) {
      const img = slider?.image;
      const imgUrl = slider?.image_url;

      if (imgUrl) return imgUrl;
      if (!img) return "";

      const apiBase = (API.API_BASE_URL || "").replace(/\/$/, "");
      const backendBase = apiBase.replace(/\/api$/i, "");

      let p = String(img);
      p = p.replace(/^public\//, "");
      p = p.replace(/^storage\//, "");
      p = p.replace(/^\/+/, "");

      return `${backendBase}/storage/${p}`;
    },





    async fetchDetail(id) {
      this.loading = true;
      this.errors = {};

      try {
        const res = await axios.get(`/sliders/${id}`, {
          headers: this.authHeaders(),
        });

        const detail = res.data?.data || res.data;

        this.form.sort = detail.sort ?? 0;
        

        this.previewUrl = this.getImageUrl(detail);
      } catch (err) {
        console.error(err);
        this.toast.error("Gagal ambil detail slider.");
      } finally {
        this.loading = false;
      }
    },

    onFileChange(e) {
      const file = e.target.files?.[0] || null;
      this.selectedFile = file;

      if (file) {
        this.previewUrl = URL.createObjectURL(file);
      }
    },

    async submitForm() {
      this.loading = true;
      this.errors = {};

      try {
        if (!this.isEdit && !this.selectedFile) {
          this.errors.image = "Image wajib diisi.";
          this.loading = false;
          return;
        }

        const fd = new FormData();
        fd.append("sort", this.form.sort ?? 0);

        if (this.selectedFile) {
          fd.append("image", this.selectedFile);
        }

        let url = "/sliders";

        // POST multipart (create)
        // Edit: POST + _method=PUT (method spoofing)
        if (this.isEdit) {
          url = `/sliders/${this.sliderIdLocal}?_method=PUT`;
        }

        await axios.post(url, fd, {
          headers: {
            ...this.authHeaders(),
            "Content-Type": "multipart/form-data",
          },
        });

        this.toast.success(this.isEdit ? "Slider updated!" : "Slider created!");
        this.$router.push("/admin/slider-builder");
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
          this.toast.error("Validasi gagal.");
        } else {
          console.error(error);
          this.toast.error("Gagal submit slider.");
        }
      } finally {
        this.loading = false;
      }
    },

    goBack() {
      this.$router.push("/admin/slider-builder");
    },
  },
  beforeUnmount() {
    if (this.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style scoped>
.dashboard-container {
  background-color: #ffffff;
  margin-top: 80px;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 5px;
  background-color: #388E3C;
  animation: loader-animation 1.5s infinite;
}

.page-title {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 20px;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
}

.preview-wrap {
  width: 260px;
  height: 150px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.08);
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@keyframes loader-animation {
  0% { width: 0; }
  50% { width: 50%; }
  100% { width: 100%; }
}
</style>
